import angular from "angular";
import { GridFilter, ISubFilter } from "./gridFilter";
import { StringFilter } from "./subFilters/StringFilter";
import { OptionsFilter } from "./subFilters/OptionsFilter";
import { NumberFilter } from "./subFilters/NumberFilter";
import { BetweenFilter } from "./subFilters/BetweenFilter";
import { DateFilter, DateFilterType } from "./subFilters/DateFilter";
import { NullFilter } from "./subFilters/NullFilter";

export const vsServiceName = "gridFilterService";
export class RecordGridFilterService {
  static $inject = ["$compile"];
  constructor(private readonly $compile: angular.ICompileService) {}

  getFilterProvider(baseFieldName: string) {
    return new FilterProvider(baseFieldName, this.$compile);
  }
}
export class FilterProvider {
  private readonly _definedFilters: Map<string, ISubFilter> = new Map<
    string,
    ISubFilter
  >();
  private readonly baseFieldName: string;
  private readonly $compile: angular.ICompileService;

  constructor(baseFieldName: string, $compile: angular.ICompileService) {
    this.$compile = $compile;
    this.baseFieldName = baseFieldName;
  }

  public defaultBooleanFilters(): FilterProvider {
    const fieldName = this.baseFieldName;
    this._definedFilters.set(
      "=",
      new OptionsFilter({
        displayName: "=",
        queryFieldNames: [fieldName],
        options: [
          { display: "No", value: false },
          { display: "Yes", value: true },
        ],
      })
    );

    return this;
  }

  public defaultStringFilters(): FilterProvider {
    const fieldName = this.baseFieldName;
    this._definedFilters.set(
      "contains",
      new StringFilter({
        displayName: "contains",
        queryFieldNames: [`${fieldName}Contains`],
      })
    );
    this._definedFilters.set(
      "=",
      new StringFilter({ displayName: "=", queryFieldNames: [fieldName] })
    );
    this._definedFilters.set(
      ">=",
      new StringFilter({
        displayName: ">=",
        queryFieldNames: [`${fieldName}GreaterThanOrEqualTo`],
      })
    );
    this._definedFilters.set(
      "<=",
      new StringFilter({
        displayName: "<=",
        queryFieldNames: [`${fieldName}LessThanOrEqualTo`],
      })
    );
    this._definedFilters.set(
      "between",
      new BetweenFilter({
        displayName: "between",
        queryFieldNames: [
          `${fieldName}GreaterThanOrEqualTo`,
          `${fieldName}LessThanOrEqualTo`,
        ],
      })
    );

    return this;
  }

  public defaultIdentityFilters(): FilterProvider {
    const fieldName = this.baseFieldName;
    this._definedFilters.set(
      "=",
      new NumberFilter({ displayName: "=", queryFieldNames: [fieldName] })
    );

    return this;
  }

  public defaultNumberFilters(): FilterProvider {
    const fieldName = this.baseFieldName;
    this._definedFilters.set(
      "=",
      new NumberFilter({ displayName: "=", queryFieldNames: [fieldName] })
    );
    this._definedFilters.set(
      ">=",
      new NumberFilter({
        displayName: ">=",
        queryFieldNames: [`${fieldName}GreaterThanOrEqualTo`],
      })
    );
    this._definedFilters.set(
      "<=",
      new NumberFilter({
        displayName: "<=",
        queryFieldNames: [`${fieldName}LessThanOrEqualTo`],
      })
    );
    this._definedFilters.set(
      "between",
      new BetweenFilter({
        displayName: "between",
        type: "number",
        queryFieldNames: [
          `${fieldName}GreaterThanOrEqualTo`,
          `${fieldName}LessThanOrEqualTo`,
        ],
      })
    );

    return this;
  }

  public defaultDateFilters(): FilterProvider {
    return this.typedDateFilters(DateFilterType.date);
  }

  public defaultDateTimeFilters(): FilterProvider {
    return this.typedDateFilters(DateFilterType.datetime);
  }

  public addNullFilter(fieldName?: string): FilterProvider {
    fieldName ??= this.baseFieldName;
    this._definedFilters.set(
      "is empty",
      new NullFilter({
        displayName: "is empty",
        queryFieldNames: [`${fieldName}IsNull`, `${fieldName}IsNotNull`],
      })
    );

    return this;
  }

  public excludeFilters(excludeKeys: string[]): FilterProvider {
    for (const filterKey of this._definedFilters.keys()) {
      if (excludeKeys.find((excludeKey) => filterKey === excludeKey)) {
        this._definedFilters.delete(filterKey);
      }
    }
    return this;
  }

  private typedDateFilters(type: DateFilterType): FilterProvider {
    const fieldName = this.baseFieldName;
    this._definedFilters.set(
      "=",
      new DateFilter({ displayName: "=", queryFieldNames: [fieldName], type })
    );
    this._definedFilters.set(
      ">=",
      new DateFilter({
        displayName: ">=",
        queryFieldNames: [`${fieldName}GreaterThanOrEqualTo`],
        type,
      })
    );
    this._definedFilters.set(
      "<=",
      new DateFilter({
        displayName: "<=",
        queryFieldNames: [`${fieldName}LessThanOrEqualTo`],
        type,
      })
    );
    this._definedFilters.set(
      "between",
      new DateFilter({
        displayName: "between",
        queryFieldNames: [
          `${fieldName}GreaterThanOrEqualTo`,
          `${fieldName}LessThanOrEqualTo`,
        ],
        type,
      })
    );
    return this;
  }

  public addFilter(displayName: string, filter: ISubFilter): FilterProvider {
    this._definedFilters.set(displayName, filter);
    return this;
  }

  public getGridFilterFactory(): () => GridFilter {
    const subFilterArray = [...this._definedFilters].map((entry) => entry[1]);
    return () => new GridFilter(subFilterArray, this.$compile);
  }
}
